import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getPublishingAvailability,
  type PublishingAvailability,
  type PublishingPlatform,
} from '../utils/publishingApi';

// One backend answer per account and page load, shared by every surface.
const requests = new Map<string, Promise<PublishingAvailability>>();
const NO_PLATFORMS: PublishingPlatform[] = [];
const UNAVAILABLE: PublishingAvailability = { platforms: NO_PLATFORMS, planIncluded: false, tiktokDirectPost: false };

const loadAvailability = (userId: string): Promise<PublishingAvailability> => {
  let request = requests.get(userId);
  if (!request) {
    request = getPublishingAvailability(userId).catch(() => {
      requests.delete(userId);
      return UNAVAILABLE;
    });
    requests.set(userId, request);
  }
  return request;
};

export const resetSocialPublishingAvailability = () => requests.clear();

/** The launched platforms and whether the account's plan includes publishing; null until the backend answers. */
export function useSocialPublishingAvailability(): PublishingAvailability | null {
  const { user } = useAuth();
  const userId = user?.uid ?? null;
  const [answer, setAnswer] = React.useState<{ userId: string; availability: PublishingAvailability } | null>(null);

  React.useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    void loadAvailability(userId).then((availability) => {
      if (active) setAnswer({ userId, availability });
    });
    return () => {
      active = false;
    };
  }, [userId]);

  if (!userId) return UNAVAILABLE;
  return answer?.userId === userId ? answer.availability : null;
}

/** The platforms launched for the signed-in account; null until the backend answers. */
export function useSocialPublishingPlatforms(): PublishingPlatform[] | null {
  const availability = useSocialPublishingAvailability();
  return availability === null ? null : availability.platforms;
}

/** Whether any platform is launched for the signed-in account; null until the backend answers. */
export function useSocialPublishingAvailable(): boolean | null {
  const platforms = useSocialPublishingPlatforms();
  return platforms === null ? null : platforms.length > 0;
}
